import { FC, Suspense, lazy, memo } from 'react'
import { Routes, Route } from 'react-router-dom'
import Box from '@mui/material/Box'
import { routes } from './routes'
import { usePersistedBackgroundLocation } from './hooks/navigation/usePersistedBackgroundLocation'
import SuspensePanel from './components/atoms/SuspensePanel'
import PrivateRoute from './components/routeSecurity/PrivateRoute'
import PublicRoute from './components/routeSecurity/PublicRoute'
import AwaitEmailVerificationRoute from './components/routeSecurity/AwaitEmailVerificationRoute'
import SuperAdminSwitchRoute from './components/routeSecurity/SuperAdminSwitchRoute'
import OnboardingRoute from './components/routeSecurity/OnboardingRoute'
import CompanyPlanningRecordPage from './components/pages/CompanyPlanningRecordPage'
import PlanningRecordPage from './components/pages/PlanningRecordPage'
import FiltersPage from './components/pages/FiltersPage'
import MapPage from './components/pages/MapPage'
import SubscriptionPage from './components/pages/SubscriptionPage'
import CompanyPaymentsPage from './components/pages/CompanyPaymentsPage'
import TemplatePage from './components/pages/TemplatePage'

const LoginPage = lazy(() => import('./components/pages/LoginPage'))
const TokenLoginPage = lazy(() => import('./components/pages/TokenLoginPage'))
const RegisterPage = lazy(() => import('./components/pages/RegisterPage'))
const DashboardPage = lazy(() => import('./components/pages/DashboardPage'))
const OnboardingPage = lazy(() => import('./components/pages/OnboardingPage'))
const AdminDashboardPage = lazy(() => import('./components/pages/AdminDashboardPage'))
const DeadLetterEventsPage = lazy(() => import('./components/pages/DeadLetterEventsPage'))
const VerifyPage = lazy(() => import('./components/pages/VerifyPage'))
const WelcomePage = lazy(() => import('./components/pages/WelcomePage'))
const ResetPasswordSuccessPage = lazy(() => import('./components/pages/ResetPasswordSuccessPage'))
const RequestPasswordResetPage = lazy(() => import('./components/pages/RequestPasswordResetPage'))
const AwaitEmailVerificationPage = lazy(
  () => import('./components/pages/AwaitEmailVerificationPage'),
)
const CompanyPlanningRecordLettersPage = lazy(
  () => import('./components/pages/CompanyPlanningRecordLettersPage'),
)
const PlanningRecordsPage = lazy(() => import('./components/pages/PlanningRecordsPage'))
const NoMatchPage = lazy(() => import('./components/pages/NoMatchPage'))

const App: FC = () => {
  const routingLocation = usePersistedBackgroundLocation()
  return (
    <Suspense fallback={<SuspensePanel />}>
      <Routes location={routingLocation}>
        <Route
          path={routes.home}
          element={
            <PrivateRoute allowedRoles={['user', 'owner', 'super-admin']}>
              <SuperAdminSwitchRoute
                superAdminPage={<AdminDashboardPage />}
                regularPage={<DashboardPage />}
              />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.companyPlanningRecordLetters}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <CompanyPlanningRecordLettersPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.planningRecords}
          element={
            <PrivateRoute allowedRoles={['super-admin']}>
              <PlanningRecordsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.retries}
          element={
            <PrivateRoute allowedRoles={['super-admin']}>
              <DeadLetterEventsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.onboarding}
          element={
            <OnboardingRoute allowedRoles={['owner']}>
              <OnboardingPage />
            </OnboardingRoute>
          }
        />
        <Route
          path={routes.awaitEmailVerification}
          element={
            <AwaitEmailVerificationRoute>
              <AwaitEmailVerificationPage />
            </AwaitEmailVerificationRoute>
          }
        />
        <Route path={routes.verify} element={<VerifyPage />} />
        <Route path={routes.welcome} element={<WelcomePage />} />
        <Route path={routes.resetPasswordSuccess} element={<ResetPasswordSuccessPage />} />
        <Route path={routes.resetPassword} element={<RequestPasswordResetPage />} />
        <Route
          path={routes.login}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={routes.tokenLogin}
          element={
            <PublicRoute>
              <TokenLoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={routes.register}
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route path={routes.blank} />
        <Route path="*" element={<NoMatchPage />} />
      </Routes>
      <Routes>
        <Route
          path={routes.companyPlanningRecord()}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <CompanyPlanningRecordPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.planningRecord()}
          element={
            <PrivateRoute allowedRoles={['super-admin']}>
              <PlanningRecordPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.filters}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <FiltersPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.map}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <MapPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.subscription}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <SubscriptionPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.companyPayments}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <CompanyPaymentsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={routes.template}
          element={
            <PrivateRoute allowedRoles={['owner']}>
              <TemplatePage />
            </PrivateRoute>
          }
        />
        <Route path="*" Component={Box} />
      </Routes>
    </Suspense>
  )
}

export default memo(App)
